import { IWizRoute } from '@wizeoo/akm-component-lib';
import { StaticFeatureEnum } from '@wizeoo/akm-common-lib';

import { AuthGuard } from './authentication.guard';
import { ContextGuard } from './context.guard';
import { NavigationCheckGuard } from './navigation-check.guard';
import { PermissionCheckGuard } from './permission.guard';
import { FallbackView } from '../../views/fallback/fallback.view';
import { RootView } from '../../views/root/root.view';

import { ChronocodeMainView } from 'src/app/views/chronocode/chronocode-main.view';
import { ChronocodeCreationView } from 'src/app/views/chronocode/creation/chronocode-creation.view';
import { ChronocodeElementView } from 'src/app/views/chronocode/element/chronocode-element.view';
import { ChronocodeHomeView } from 'src/app/views/chronocode/home/chronocode-home.view';
import { ConnectView } from 'src/app/views/connect/connect.view';
import { DashboardDataView } from 'src/app/views/dashboard/datas/dashboard-data.view';
import { FormatView } from 'src/app/views/formats/format.view';
import { FreedataView } from 'src/app/views/freedatas/freedata.view';
import { IncExcView } from 'src/app/views/incexc/incexc.view';
import { InitView } from 'src/app/views/main/init.view';
import { MeasureView } from 'src/app/views/measure/measure.view';
import { NewtabView } from 'src/app/views/newtab/newtab.view';
import { ProfileView } from 'src/app/views/profile/profile.view';
import { StyleView } from 'src/app/views/style/style.view';
import { UserView } from 'src/app/views/user/user.view';
import { VisionGenerateView } from 'src/app/views/vision/vision-generate.view';
import { VisionView } from 'src/app/views/vision/vision.view';
import { WgView } from 'src/app/views/wg/wg.view';
import { TemplatingView } from '../../views/templating/templating.view';
import { UserTabView } from '../../views/user-tab/user-tab.view';
import { ScenarioView } from 'src/app/views/scenarii/scenario.view';
import { CssRenderView } from 'src/app/views/cssrender/cssrender.view'; 
import { WorkflowView } from 'src/app/views/workflow/workflow.view';
import { MatrixView } from 'src/app/views/matrix/matrix.view';


/**
 * Les guards des enfants pour les vues principales en dehors de la fallback
 */
const childrenGuards: IWizRoute = { canActivate: [AuthGuard, PermissionCheckGuard, NavigationCheckGuard] };

/**
 * gestion des emplacements "navigables", des containers de fonctions,
 * des noms de vues et des boutons d'action
 */
export class RouteEngine {

  // les couleurs de base pour les vues admin
  public static TAB_COLOR_BASIC_TOOL: string = '#502b7a';
  public static TAB_COLOR_DARK_VISION: string = '#F58022';
  public static TAB_COLOR_BASIC_VISION: string = '#818181';


  // Normalisation des noms de vues
  public static FREE_DATA_VIEW: string = 'freedata';
  public static CHRONO_VIEW: string = 'chronocode';
  public static CHRONO_HOME_VIEW: string = 'chronohome';
  public static CHRONO_CREATION_VIEW: string = 'chronocreation';
  public static CHRONO_LEVEL_VIEW: string = 'chronolevel';
  public static CHRONO_ELT_VIEW: string = 'chronoelement';
  public static BASELINE_VIEW: string = 'baseline';

  public static LANGUAGE_VIEW: string = 'language';
  public static COUNTRY_VIEW: string = 'country';
  public static ROLE_VIEW: string = 'role';
  public static PROFILE_VIEW: string = 'profile';

  public static DASHBOARD_DATA_VIEW: string = 'dashboard-data';
  public static MEASURE_VIEW: string = 'measure'; // vue de gestion des mesures disponibles pour les BUs
  public static WG_VIEW: string = 'weightgrid'; // vue de gestion des grilles de poids
  public static STYLE_VIEW: string = 'style'; // vue de gestion des styles
  public static FORMAT_VIEW: string = 'format'; // vue de gestion des format
  public static CSSRENDER_VIEW: string = 'cssrender'; // vue de gestion des rendus css
  public static WORKFLOW_VIEW: string = 'workflow'; // vue de gestion des workflows
  public static VISION_VIEW: string = 'vision'; // vue de gestion des visions
  public static VISION_GENERATE_VIEW: string = 'vision-generate'; // vue de génération des lignes de steps
  public static CONNECT_VIEW: string = 'connect'; // vue d'édition d'un TCD
  public static MATRIX_VIEW: string = 'matrix'; // vue de gestion des matrices de données

  public static INIT_VIEW: string = 'init'; // vue de démarrage avec chargement du contexte
  public static NEWTAB_VIEW: string = 'newtab'; // vue d'ajout de vue (sans chargement du contexte)
  public static TEMPLATING_VIEW: string = 'templating'; // vue templating pour prépartion des widgets
  public static USER_TAB_VIEW: string = 'user-tab-view'; // vue pour les tabs custom du user
  public static USER_VIEW: string = 'user-view'; // vue de création et configuration des users

  public static INCEXC_VIEW: string = 'incexc-view'; // vue de gestion des exclusions et inclusions
  public static FALLBACK_VIEW: string = 'context'; // vue de fallback en cas de contexte incohérent

  public static SCENARIO_VIEW: string = 'scenario'; // vue de gestion des scenarii

  /**
   * construction de l'arbre de navigation statique
   * ATTENTION : le path doit être UNIQUE pour permettre une navigation sans FullPath
   */
  public static getRouteDefinition(): IWizRoute[] {
    const routes: IWizRoute[] = [
      {
        path: '',
        component: RootView,
        canActivate: [AuthGuard, ContextGuard],
        resolve: { ctx: ContextGuard },
        children: [
          {
            label: '$.dashboard.data.title.$',
            path: RouteEngine.DASHBOARD_DATA_VIEW,
            component: DashboardDataView,
            ...childrenGuards,
          },
          {
            label: '$.user.title.$',
            featureCode: StaticFeatureEnum.VISION_USER,
            path: RouteEngine.USER_VIEW,
            component: UserView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_BASIC_TOOL,
            description: '$.user.description.menu.$'
          },
          {
            label: '$.profile.title.$',
            featureCode: StaticFeatureEnum.VISION_PROFILE,
            path: RouteEngine.PROFILE_VIEW,
            component: ProfileView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_BASIC_TOOL,
            description: '$.profile.description.menu.$'
          },

          {
            label: '$.chronocode.title.$',
            featureCode: StaticFeatureEnum.CHRONOCODE,
            path: RouteEngine.CHRONO_VIEW,
            component: ChronocodeMainView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_BASIC_TOOL,
            description: '$.chronocode.description.menu.$',
            children: [
              {
                label: '$.chronocode.home.title.$',
                featureCode: StaticFeatureEnum.CHRONOCODE,
                path: RouteEngine.CHRONO_HOME_VIEW,
                component: ChronocodeHomeView,
                ...childrenGuards,
              },
              {
                label: '$.chronocode.creation.title.$',
                featureCode: StaticFeatureEnum.CHRONOCODE,
                path: RouteEngine.CHRONO_CREATION_VIEW,
                component: ChronocodeCreationView,
                ...childrenGuards,
              },
              {
                label: '$.chronocode.element.title.$',
                featureCode: StaticFeatureEnum.CHRONOCODE,
                path: RouteEngine.CHRONO_ELT_VIEW,
                component: ChronocodeElementView,
                ...childrenGuards,
              },
              { path: '', redirectTo: RouteEngine.CHRONO_HOME_VIEW, pathMatch: 'full' },
            ],
          },
          {
            label: '$.freedata.title.$',
            featureCode: StaticFeatureEnum.FREEDATA,
            path: RouteEngine.FREE_DATA_VIEW,
            component: FreedataView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_BASIC_TOOL,
            description: '$.freedata.description.menu.$'
          },
          {
            label: '$.incexc.title.$',
            featureCode: StaticFeatureEnum.INCEXC,
            path: RouteEngine.INCEXC_VIEW,
            component: IncExcView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_BASIC_VISION,
            description: '$.incexc.description.menu.$'
          },
          {
            label: '$.measure.title.$',
            featureCode: StaticFeatureEnum.MEASURE,
            path: RouteEngine.MEASURE_VIEW,
            component: MeasureView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_DARK_VISION,
            description: '$.measure.description.menu.$'
          },
          {
            label: '$.scenario.title.$',
            featureCode: StaticFeatureEnum.SCENARIO,
            path: RouteEngine.SCENARIO_VIEW,
            component: ScenarioView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_DARK_VISION,
            description: '$.scenario.description.menu.$'
          },
          {
            label: '$.wg.title.$',
            featureCode: StaticFeatureEnum.WG,
            path: RouteEngine.WG_VIEW,
            component: WgView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_DARK_VISION,
            description: '$.wg.description.menu.$'
          },
          {
            label: '$.connect.title.$',
            featureCode: StaticFeatureEnum.CONNECT,
            path: RouteEngine.CONNECT_VIEW,
            component: ConnectView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_BASIC_TOOL,
            description: '$.connect.description.menu.$'
          },
          {
            label: '$.style.title.$',
            featureCode: StaticFeatureEnum.STYLE,
            path: RouteEngine.STYLE_VIEW,
            component: StyleView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_BASIC_VISION,
            description: '$.style.description.menu.$'
          },
          {
            label: '$.format.title.$',
            featureCode: StaticFeatureEnum.FORMAT,
            path: RouteEngine.FORMAT_VIEW,
            component: FormatView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_BASIC_VISION,
            description: '$.format.description.menu.$'
          },
          {
            label: '$.cssrender.title.$',
            featureCode: StaticFeatureEnum.CSSRENDER,
            path: RouteEngine.CSSRENDER_VIEW,
            component: CssRenderView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_BASIC_VISION,
            description: '$.cssrender.description.menu.$'
          },
          {
            label: '$.workflow.title.$',
            featureCode: StaticFeatureEnum.WORKFLOW,
            path: RouteEngine.WORKFLOW_VIEW,
            component: WorkflowView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_BASIC_VISION,
            description: '$.workflow.description.menu.$'
          },
          {
            label: '$.matrix.title.$',
            featureCode: StaticFeatureEnum.MATRIX,
            path: RouteEngine.MATRIX_VIEW,
            component: MatrixView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_BASIC_VISION,
            description: '$.matrix.description.menu.$'
          },
          {
            label: '$.vision.title.$',
            featureCode: StaticFeatureEnum.VISION,
            path: RouteEngine.VISION_VIEW,
            component: VisionView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_DARK_VISION,
            description: '$.vision.description.menu.$'
          },
          {
            label: '$.templating.title.$',
            featureCode: StaticFeatureEnum.TEMPLATE,
            path: RouteEngine.TEMPLATING_VIEW,
            component: TemplatingView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_BASIC_VISION,
            description: '$.format.description.menu.$'
          },
          {
            label: '$.vision.generate.title.$',
            featureCode: StaticFeatureEnum.VISION_GENERATE,
            path: RouteEngine.VISION_GENERATE_VIEW,
            component: VisionGenerateView,
            ...childrenGuards,
            showInTabs: true,
            color: RouteEngine.TAB_COLOR_DARK_VISION,
            description: '$.templating.description.menu.$'
          },

          // bouton + pour affichage du menu "tabs"
          { 
            label: '$.newtab.title.$', 
            path: RouteEngine.NEWTAB_VIEW, 
            component: NewtabView, 
            ...childrenGuards,
            data: { contextFree : true } 
          },
          
          // accesseur pour les vues custom
          {
            label: '$.user.tab.title.$',
            path: `${RouteEngine.USER_TAB_VIEW}/:code`,
            component: UserTabView,
            ...childrenGuards,
          },

          // vue de démarrage pour chargement du contexte
          { 
            path: RouteEngine.INIT_VIEW, 
            component: InitView, 
            ...childrenGuards, 
          }, 

          // { path: '', redirectTo: RouteEngine.DASHBOARD_DATA_VIEW, pathMatch: 'full' },
          { 
            path: '', 
            redirectTo: RouteEngine.INIT_VIEW, 
            pathMatch: 'full'
          },
          // redirections en cas d'échec
        ],
      },
      {
        label: '$.fallback.title.$',
        path: `${RouteEngine.FALLBACK_VIEW}`,
        component: FallbackView,
        canActivate: [AuthGuard],
      },
      // { path: '', redirectTo: RouteEngine.DASHBOARD_DATA_VIEW, pathMatch: 'full' },
      // { path: '**', redirectTo: RouteEngine.DASHBOARD_DATA_VIEW, pathMatch: 'full' },
      { 
        path: '**', 
        redirectTo: RouteEngine.INIT_VIEW, 
        pathMatch: 'full'
      },
    ];

    return routes;
  }
}
